import { imageByPath } from '@/assets';
import { floors } from '@/content/floors';
import type { StoryScene } from '@/content/story';
import type { Orientation } from '@/lib/orientation';

/** Agent Alex's intro sprites (art/characters/agent-alex), one canvas for all expressions. */
export type AlexPose = 'wave' | 'present' | 'whisper' | 'worried' | 'rrr';
export const POSES: readonly AlexPose[] = ['wave', 'present', 'whisper', 'worried', 'rrr'];

/** What a story scene shows: Alex's expression, the foe on the screen, the camera close on Alex. */
export const SCENES: Record<StoryScene, { alex: AlexPose; foe: boolean; close?: boolean }> = {
  welcome: { alex: 'wave', foe: false },
  screen: { alex: 'present', foe: false },
  'whisper-close': { alex: 'whisper', foe: false, close: true },
  cat: { alex: 'present', foe: true },
  'cat-whisper': { alex: 'whisper', foe: true },
  'cat-worried': { alex: 'worried', foe: true },
  'cat-excited': { alex: 'rrr', foe: true },
};

/** A box on the room painting, in percent of its width and height. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A screen's glass on the painting: its corners (top left, top right, bottom right, bottom left). */
export type Quad = readonly [
  readonly [number, number],
  readonly [number, number],
  readonly [number, number],
  readonly [number, number],
];

/**
 * Where things are on each room painting (art/backgrounds/intro/room-*), in percent: the screen's
 * glass (at an angle, so as its four corners, measured on the painting), Alex's sprite box, the
 * point the close-up zooms to (his face), and where the painting is cropped when the viewport is
 * narrower than it (`anchorX`: 0 keeps the left edge, 1 the right; phones in portrait crop about
 * 18% of the width, which must not cut off the screen).
 */
export const LAYOUT: Record<
  Orientation,
  { screen: Quad; alex: Box; face: [number, number]; anchorX: number }
> = {
  portrait: {
    screen: [
      [46.5, 19.5],
      [96, 17.5],
      [96.4, 66.8],
      [45.8, 67],
    ],
    alex: { left: 12, top: 44, width: 50, height: 56 },
    face: [35, 60],
    anchorX: 0.9,
  },
  landscape: {
    screen: [
      [53.8, 10.4],
      [92.6, 5.4],
      [93, 88.6],
      [52.4, 88.1],
    ],
    alex: { left: 6, top: 10, width: 46, height: 90 },
    face: [28, 30],
    anchorX: 0.6,
  },
};

/** The first foe, shown on the screen. */
export const FOE = floors[0]?.foe;

export const roomImage = (orientation: Orientation) =>
  imageByPath[`backgrounds/intro/room-${orientation}`];
export const alexImage = (pose: AlexPose) => imageByPath[`characters/agent-alex/${pose}`];

/** Every image the intro may show in this orientation, to fetch up front. */
export function sceneImages(orientation: Orientation): string[] {
  const urls = [roomImage(orientation)?.url, FOE?.portraits.disguised.url];
  for (const pose of POSES) urls.push(alexImage(pose)?.url);
  return urls.filter((u): u is string => Boolean(u));
}
